import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Modal, Input, Table } from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';
import { getPagedStoresForDropdown } from '../../services/storeserivce';
import { StoreDropdownDto } from '../../types/storetypes';
import FormButton from './formbutton';
import { useAuth } from '../../auth/authcontext';

const { Text, Paragraph } = Typography;
const { Search } = Input;

export interface StoreSelectionManagerProps {
    assignedStores: string[];
    onChange: (stores: string[]) => void;
    title?: string;
    description?: string;
    modalTitle?: string;
    modalDescription?: string;
}

const StoreSelectionManager: React.FC<StoreSelectionManagerProps> = ({
    assignedStores,
    onChange,
    title = "Store Assignments",
    description = "Manage which stores are associated.",
    modalTitle = "Manage Stores",
    modalDescription = "Search and select stores."
}) => {
    const { isReadOnly } = useAuth();
    const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
    const [storeSearchText, setStoreSearchText] = useState("");
    const [storePageNumber, setStorePageNumber] = useState(1);
    const [storeTotalCount, setStoreTotalCount] = useState(0);
    const [storeTableLoading, setStoreTableLoading] = useState(false);
    const [pagedAvailableStores, setPagedAvailableStores] = useState<StoreDropdownDto[]>([]);

    useEffect(() => {
        if (isStoreModalOpen && pagedAvailableStores.length === 0) {
            fetchPagedStores(1, "");
        }
    }, [isStoreModalOpen, pagedAvailableStores.length]);

    const fetchPagedStores = async (page = storePageNumber, search = storeSearchText) => {
        setStoreTableLoading(true);
        try {
            const res = await getPagedStoresForDropdown({ pageNumber: page, pageSize: 15, searchText: search });
            setPagedAvailableStores(res.data);
            setStoreTotalCount(res.totalCount);
            setStorePageNumber(page);
        } catch (error) {
            console.error("Failed to load paginated stores:", error);
        } finally {
            setStoreTableLoading(false);
        }
    };

    return (
        <Card title={title} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '8px' }}>
            <Paragraph>
                {description}
            </Paragraph>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <FormButton 
                    title="Manage Assigned Stores" 
                    icon={<EditOutlined />} 
                    onClick={() => setIsStoreModalOpen(true)} 
                />
                <Text strong style={{ fontSize: '16px' }}>
                    {assignedStores.length} {assignedStores.length === 1 ? 'store' : 'stores'} currently assigned
                </Text>
            </div>

            <Modal
                title={modalTitle}
                open={isStoreModalOpen}
                onCancel={() => setIsStoreModalOpen(false)}
                footer={[
                    <FormButton
                        key="close"
                        title="Done"
                        icon={<CheckOutlined />}
                        onClick={() => setIsStoreModalOpen(false)}
                    />
                ]}
                width={900}
                style={{ top: 20, maxWidth: '95vw' }}
                destroyOnClose
            >
                <Paragraph>
                    {modalDescription}
                </Paragraph>
                <div style={{ display: 'flex', gap: '16px', height: '60vh', minHeight: '400px', maxHeight: '700px', overflow: 'hidden' }}>
                    {/* Left Side: Server Paginated Table */}
                    <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '250px' }}>
                        <Search 
                            placeholder="Search store name or number..." 
                            onSearch={(val) => { setStoreSearchText(val); fetchPagedStores(1, val); }}
                            allowClear
                            enterButton
                        />
                        <Table 
                            rowKey="storeNumber"
                            rowSelection={isReadOnly ? undefined : {
                                selectedRowKeys: assignedStores,
                                preserveSelectedRowKeys: true,
                                onChange: (keys) => onChange(keys as string[])
                            }}
                            columns={[
                                { title: 'Store Number', dataIndex: 'storeNumber', width: 130, render: (t) => <Text strong>#{t}</Text> },
                                { title: 'Store Name', dataIndex: 'storeDisplayName' }
                            ]}
                            dataSource={pagedAvailableStores}
                            pagination={{
                                current: storePageNumber,
                                pageSize: 15,
                                total: storeTotalCount,
                                onChange: (page) => fetchPagedStores(page, storeSearchText),
                                showSizeChanger: false
                            }}
                            loading={storeTableLoading}
                            size="small"
                            scroll={{ x: 'max-content', y: 'calc(60vh - 160px)' }}
                        />
                    </div>

                    {/* Right Side: Selected Stores List */}
                    <div style={{ flex: '0 1 300px', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '16px', borderLeft: '1px solid #f0f0f0', paddingLeft: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>{assignedStores.length} Selected</Text>
                            {!isReadOnly && (
                                <Button type="link" danger size="small" onClick={() => onChange([])}>Clear All</Button>
                            )}
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '8px' }}>
                            {assignedStores.map(storeNum => (
                                <div key={storeNum} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '8px 12px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                    <Text style={{ fontFamily: 'monospace', fontWeight: 600 }}>#{storeNum}</Text>
                                    {!isReadOnly && (
                                        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onChange(assignedStores.filter(s => s !== storeNum))} />
                                    )}
                                </div>
                            ))}
                            {assignedStores.length === 0 && (
                                <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '40px' }}>
                                    No stores selected
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </Card>
    );
};

export default StoreSelectionManager;
